import { CancelTokenSource } from 'axios'
import {
  GetDoctors,
  GetFacilities,
  GetProvidersByPracticeIdOpenSearch,
  GetProvidersFilter,
  GetProvidersSearch,
  SearchProvidersByPracticeIdParams,
} from 'src/api/types'
import { CATEGORY } from 'src/refactor/helpers/constants'
import {
  IFacility,
  IPractitioner,
  IPractice,
  TSpecialty,
  TSearchType,
  TPublishStatus,
} from 'src/shared/types'
import { AtLeastOne } from 'src/types/helpers'

export interface ItemData {
  id: string
  practiceName: string
  distance: number
  address1: string
  address2?: string
  fullAddress: string
  lat: number
  long: number
  phone: string
  itemType: TSearchType
  npi?: string
  isPreferred: boolean
  network?: string
  fullName?: string
  primarySpecialty?: string
  localUuid?: string
  hasVirtualCare?: boolean
  isVirtualCareOnly?: boolean
  publish?: TPublishStatus
  displayPriority?: number
  city: string
  state: string
  zipCode: string
  taxonomyDisplay?: string
  taxonomies?: {
    rank: number
    taxonomyCode: string
    taxonomyDescription: string
  }[]
}

export interface State {
  category: CATEGORY
  isLoading: boolean
  filter: GetProvidersFilter
  search: GetProvidersSearch
  itemType: TSearchType
  listView: 'list' | 'map'
  hasFilterChanged: boolean
  isFirstSearch: boolean
  specialties?: TSpecialty[]
  results?: (GetDoctors | GetFacilities)['data']
  unfilteredItems?: ItemData[]
  displayItems?: ItemData[]
  totalCount?: number
  isVirtualCareAvailable?: boolean
  isAcceptingNewPatients?: boolean
  isPractitionerListLoading?: boolean
  loadingPractice?: ItemData['id']
  expandedPractice?: ItemData['id'] | null
  viewPractitionerList: {
    practice: ItemData['id']
    list?: GetProvidersByPracticeIdOpenSearch['data']
  }[]
  specialtyFilters: {
    name: string
    code: string
  }[]
  cancelTokenSource?: CancelTokenSource
  errorMsg?: string
  previousDistance: number
  isRequestCanceled: boolean
  maxTotalCount?: number
  maxTotalCountProviders?: number
  maxTotalCountPractices?: number
}

export type SearchData = AtLeastOne<
  {
    search: GetProvidersSearch
    filter: GetProvidersFilter
    shouldReplaceSearch?: boolean
    isFirstSearch?: boolean
  },
  'search' | 'filter'
>

export interface ActionFunctions {
  readonly search: (data: SearchData) => Promise<void>
  readonly getProvidersByPractice: (data: {
    search: SearchProvidersByPracticeIdParams
    filter: GetProvidersFilter
    cancelTokenSource?: CancelTokenSource
  }) => Promise<void>
  readonly cancelRequest: () => void
  readonly toggleListView: () => 'list' | 'map'
  readonly filter: (data?: Partial<State['filter']>) => void
  readonly setExpandedPractice: (practiceId: ItemData['id'] | null) => void
}

export type Context = readonly [State, ActionFunctions]

export enum ActionTypes {
  SEARCH_PROVIDERS = 'SEARCH_PROVIDERS',
  SEARCH_PROVIDERS_FULFILLED = 'SEARCH_PROVIDERS_FULFILLED',
  SEARCH_RADIUS_INCREASE = 'SEARCH_RADIUS_INCREASE',
  SEARCH_PROVIDERS_FAILED = 'SEARCH_PROVIDERS_FAILED',
  ON_REQUEST_SEARCH = 'ON_SEARCH',
  ON_REQUEST_FILTER = 'ON_REQUEST_FILTER',
  ON_SEARCH = 'ON_SEARCH',
  ON_FILTER = 'ON_FILTER',
  CANCEL_REQUEST = 'CANCEL_REQUEST',
  TOGGLE_LIST_VIEW = 'TOGGLE_LIST_VIEW',
  GET_PRACTITIONERS = 'GET_PRACTITIONERS',
  GET_PROVIDERS_BY_PRACTICE_ID_SUCCEED = 'GET_PROVIDERS_BY_PRACTICE_ID_SUCCEED',
  GET_PROVIDERS_BY_PRACTICE_ID_FAILED = 'GET_PROVIDERS_BY_PRACTICE_ID_FAILED',
  SET_EXPANDED_PRACTICE = 'SET_EXPANDED_PRACTICE',
}

export type Action =
  | {
      type: ActionTypes.SEARCH_PROVIDERS
      payload: {
        filter?: GetProvidersFilter
        search?: GetProvidersSearch
        cancelTokenSource?: CancelTokenSource
      }
    }
  | {
      type: ActionTypes.SEARCH_PROVIDERS_FULFILLED
      payload: {
        data: GetDoctors | GetFacilities
        updatedFilter: GetProvidersFilter
        maxTotalCount?: number
        countProviders?: {
          maxTotalCount?: number
          maxTotalCountPractices?: number
          maxTotalCountProviders?: number
        }
        countItems?: {
          maxTotalCount?: number
          maxTotalCountPractices?: number
          maxTotalCountProviders?: number
        }
      }
    }
  | {
      type: ActionTypes.SEARCH_RADIUS_INCREASE
      payload: {
        distance: number
        errorMsg: string
        previousDistance: number
      }
    }
  | {
      type: ActionTypes.SEARCH_PROVIDERS_FAILED
      payload: unknown
    }
  | {
      type: ActionTypes.ON_REQUEST_SEARCH
      payload: State['search']
    }
  | {
      type: ActionTypes.ON_SEARCH
      payload: IPractitioner[] | IFacility[] | IPractice[]
    }
  | {
      type: ActionTypes.ON_REQUEST_FILTER
      payload: State['filter']
    }
  | {
      type: ActionTypes.ON_FILTER
      payload: ItemData[]
    }
  | {
      type: ActionTypes.CANCEL_REQUEST
      payload?: never
    }
  | {
      type: ActionTypes.TOGGLE_LIST_VIEW
      payload?: never
    }
  | {
      type: ActionTypes.GET_PRACTITIONERS
      payload: ItemData['id']
    }
  | {
      type: ActionTypes.GET_PROVIDERS_BY_PRACTICE_ID_SUCCEED
      payload: GetProvidersByPracticeIdOpenSearch['data']
    }
  | {
      type: ActionTypes.GET_PROVIDERS_BY_PRACTICE_ID_FAILED
      payload: unknown
    }
  | {
      type: ActionTypes.SET_EXPANDED_PRACTICE
      payload: {
        practiceId: ItemData['id'] | null
      }
    }
